import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { api } from "../lib/api";
import { useContent } from "../context/ContentContext";
import { coverSrc } from "../lib/images";

const MIN_MS = 1800;
const MAX_MS = 4000;
const EASE = [0.16, 1, 0.3, 1];

// Phones get fewer, wider columns so the covers stay readable rather than becoming stripes.
const columnCount = () => {
  const w = typeof window === "undefined" ? 1280 : window.innerWidth;
  return w < 640 ? 3 : w < 1024 ? 5 : 7;
};

const STATUS = [
  "Turning on the library lights",
  "Sorting the bookshelves",
  "Arranging the books",
  "Dusting the spines",
  "Finding your bookmark",
];

// Each column drifts at its own pace, alternating direction, so the wall never marches in step.
const COLUMN_SECONDS = [58, 44, 70, 50, 64, 46, 54];

export default function Splash() {
  const { content } = useContent();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(true);
  const [covers, setCovers] = useState([]);
  const [status, setStatus] = useState(0);
  const [cols] = useState(columnCount);
  const hero = content?.hero?.bg_image;

  useEffect(() => {
    api.get("/covers?limit=56").then((r) => setCovers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (reduce) return undefined;
    const t = setInterval(() => setStatus((s) => (s + 1) % STATUS.length), 1150);
    return () => clearInterval(t);
  }, [reduce]);

  useEffect(() => {
    const started = performance.now();
    let done = false;
    const lift = () => {
      if (done) return;
      done = true;
      setTimeout(() => setOpen(false), Math.max(0, MIN_MS - (performance.now() - started)));
    };
    const cap = setTimeout(lift, MAX_MS);
    if (!hero) return () => clearTimeout(cap);
    const img = new Image();
    img.src = coverSrc(hero, "hero");
    if (img.decode) img.decode().then(lift).catch(lift);
    else { img.onload = lift; img.onerror = lift; }
    return () => clearTimeout(cap);
  }, [hero]);

  // Deal the covers into columns, then repeat each column twice so the drift can loop seamlessly.
  const columns = useMemo(() => {
    if (!covers.length) return [];
    const lanes = Array.from({ length: cols }, () => []);
    covers.forEach((c, i) => lanes[i % cols].push(c));
    return lanes.map((c) => (c.length < 5 ? [...c, ...c, ...c].slice(0, 5) : c));
  }, [covers, cols]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="splash"
          initial={{ opacity: 1 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 1.05 }}
          transition={{ duration: reduce ? 0.3 : 0.8, ease: EASE }}
          className="fixed inset-0 z-[200] bg-ink overflow-hidden"
        >
          {/* the wall: edge-to-edge columns of covers, drifting */}
          <motion.div
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className="absolute inset-0 flex gap-3 sm:gap-4 justify-center"
            style={{ transform: "scale(1.18) rotate(-4deg)", transformOrigin: "center" }}
          >
            {columns.map((col, i) => (
              <div key={i} className="flex-1 min-w-0 overflow-hidden" data-testid={`splash-wall-column-${i}`}>
                <motion.div
                  className="flex flex-col gap-3 sm:gap-4"
                  initial={{ y: i % 2 ? "-50%" : "0%" }}
                  animate={reduce ? {} : { y: i % 2 ? ["-50%", "0%"] : ["0%", "-50%"] }}
                  transition={{ duration: COLUMN_SECONDS[i], repeat: Infinity, ease: "linear" }}
                >
                  {[...col, ...col].map((src, j) => (
                    <div key={j} className="w-full aspect-[2/3] overflow-hidden bg-ink-800">
                      <img
                        src={coverSrc(src, "thumb")}
                        alt=""
                        loading="eager"
                        decoding="async"
                        className="w-full h-full object-cover"
                        style={{ opacity: 0.55, filter: "blur(1px)" }}
                      />
                    </div>
                  ))}
                </motion.div>
              </div>
            ))}
          </motion.div>

          {/* vignette pushes the wall back so the lockup always reads; grain dithers the falloff */}
          <div aria-hidden="true" className="absolute inset-0" style={{ background: "radial-gradient(circle at center, rgb(10 9 8 / 0.97) 0%, rgb(10 9 8 / 0.88) 26%, rgb(10 9 8 / 0.62) 58%, rgb(10 9 8 / 0.45) 100%)" }} />
          <div aria-hidden="true" className="grain" style={{ opacity: 0.1 }} />

          <div className="relative h-full flex flex-col items-center justify-center px-6">
            <motion.div
              initial={{ opacity: 0, y: reduce ? 0 : 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="flex items-center gap-4"
            >
              <span aria-hidden="true" className="w-11 h-11 border border-brass/60 flex items-center justify-center font-serif text-lg text-brass">TS</span>
              <span className="font-serif font-light tracking-tight text-vellum text-[clamp(1.6rem,3.4vw,2.6rem)]">
                Tirthak&apos;s <span className="italic text-brass">Library</span>
              </span>
            </motion.div>

            {/* the rail */}
            <div className="mt-9 h-[2px] w-56 sm:w-72 bg-line/80 overflow-hidden" data-testid="splash-rail">
              <motion.div
                initial={{ x: "-100%" }}
                animate={reduce ? { x: "0%" } : { x: ["-100%", "0%", "100%"] }}
                transition={reduce ? { duration: 0.3 } : { duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                className="h-full w-full bg-gradient-to-r from-transparent via-crimson to-transparent"
              />
            </div>

            <div className="mt-6 h-5 relative w-full flex justify-center">
              <AnimatePresence mode="wait">
                <motion.p
                  key={status}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  data-testid="splash-status"
                  className="absolute font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-vellum-muted"
                >
                  {STATUS[status]}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
